import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import Slider from 'react-slick';
import styled from 'styled-components';
import { PRODUCT_RATING_WRAPPER, PRODUCT_RATING } from 'elc-service-view-names';
import { ProductImages, IProductImages } from 'elc-images';
import { PriceFormatted } from 'elc-formatters';
import { SliderView } from 'elc-slider';
import { Desktop } from 'elc-layouts';
import { ServiceView, config, ITranslationsCollection, translate } from 'elc-service';
import { ContentSpacing, Colors, Breakpoints, breakpoint } from 'elc-base-theme';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';
import { IProductRatingConfig } from '../../../exported/product-rating/ProductRatingWrapper';
import {
    ProductQuantitySelector,
    IProductQuantitySelector
} from '../../../exported/product-quantity-selector/ProductQuantitySelector';
import ProductCTA, {
    IProductAddToBag,
    IProductCTAConfig,
    IWaitlist
} from '../../../exported/product-cta/ProductCTA';
import ProductShadePicker from '../../../exported/product-shade-picker/ProductShadePicker';
import { IAsset } from '../../domain/entities/Asset';
import { Sku } from '../../domain/entities/Sku';
import {
    IShadeSwatches,
    IShadePickerDropdown,
    IShadePickerConfig
} from '../../interfaces/IShadePicker';
import {
    IImageCarouselSettings,
    ICarouselSettings,
    IShortDescriptionConfig
} from '../../interfaces/IProdcat';
import { ProductPriceFormatted } from '../common/ProductPriceFormatted';
import { ProductShadePickerComponent } from '../common/ProductShadePickerComponent';
import { ProductItemWrapper } from '../common/ProductItemWrapper';
import {
    DEFAULT_IMAGE_CAROUSEL_SETTINGS,
    DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS
} from '../../constants/ImageThumbnailCarouselSettings';
import { SHADE_PICKER_TYPE } from '../../constants/ShadePickerType';
import { ComponentTypes } from '../../constants/ComponentTypes';
import { InventoryStatusMessage } from '../common/InventoryStatusMessage';
import { ADD_TO_BAG, LINK } from '../../constants/CTATypes';
import { TOGGLE_TYPE } from '../../constants/AutoReplenish';
import { IFeatures } from '../../constants/Features';
import { StyledSliderSection } from '../product-full/ProductFullCarousel';
import ProductInstallmentsPrice, {
    IProductInstallmentsPrice
} from '../product-installments-price/ProductInstallmentsPrice';
import { ProductThumbnailsCarousel } from '../product-full/ProductThumbnailsCarousel';
import { FavoriteButton } from '../product-favorites/FavoriteButton';
import { ProductName, IProductName } from '../product-brief/ProductName';
import { ProductDescription, IProductDescription } from '../product-brief/ProductDescription';
import { ProductSizePicker, ISizePicker } from '../product-size-picker/ProductSizePicker';
import { ProductBadge, IProductBadge } from '../product-badge/ProductBadge';
import {
    ProductAutoReplenish,
    IProductAutoReplenish
} from '../product-auto-replenish/ProductAutoReplenish';
import { classes } from '../../utils/Classes';
import { PreorderMessage } from '../common/PreorderMessage';
import { IProductNotifyMeCTA, NotifyMeCTA } from '../product-notify-me/NotifyMeCTA';
import { IFreeShippingAndReturnConfig } from '../../interfaces/ITranslations';

export interface IProductQuickViewContentConfig {
    features?: IFeatures;
    shadeSwatches?: IShadeSwatches;
    showProductPriceInstallments?: boolean;
    ratingsEnabled?: boolean;
    imageCarouselSettings?: IImageCarouselSettings;
    showInventoryMessaging?: string[];
    waitlist?: IWaitlist;
    hideProductBadge?: boolean;
    enableNotifyMe?: boolean;
    isCoreSite?: boolean;
    freeShippingAndReturnConfig?: IFreeShippingAndReturnConfig;
}

export interface IProductPriceRowWrapper {
    className?: string;
}

export interface IProductPriceRow extends IProductPriceRowWrapper {
    selectedSku: Sku;
}

export interface IProductPriceRowWrapper {
    className?: string;
}

export interface IProductPriceRow extends IProductPriceRowWrapper {
    selectedSku: Sku;
}

export interface IProductQuickViewContent extends IProductBadge {
    onCloseButtonClick(): void;
    handleFavoriteClick(): void;
    isProductShaded: boolean;
    productImageProps: IProductImages;
    productNameProps: IProductName;
    shortDescriptionLocation: IShortDescriptionConfig;
    productShortDescriptionProps: IProductDescription;
    productRatingProps: IProductRatingConfig;
    productShadePickerProps: IShadePickerDropdown;
    productSizePickerProps: ISizePicker;
    productQuantitySelectorProps: IProductQuantitySelector;
    productPriceRowProps: IProductPriceRow;
    productAddToBagProps: IProductAddToBag;
    hideProductRating?: boolean;
    isProductReplenishable: boolean;
    productAutoReplenishProps: IProductAutoReplenish;
    customSizeImages: IAsset[];
    largeImages: IAsset[];
    selectedSku: Sku;
    autoReplenishOptions: number[];
    autoReplenishToggleType?: TOGGLE_TYPE;
    hideProductPricePerUnit?: boolean;
    showProductQuantitySelector?: boolean;
    enableAutoReplenishment?: boolean;
    config?: IProductQuickViewContentConfig;
    translations?: ITranslationsCollection;
    productQuickViewImageCarouselSettings: ICarouselSettings;
    thumbnailsCarouselSettings: ICarouselSettings;
    isImageCarouselDisabled: boolean;
    ctaType?: string;
    isThumbnailsCarouselEnabled: boolean;
}

const ProductQuickViewContentWrapper = styled('div')`
    display: flex;
    align-items: center;
    flex-direction: column;
    clear: both;
    ${breakpoint('desktop')`
        flex-direction: row;
        clear: none;
    `};
    .elc-product-images-wrapper {
        height: 100%;
        background-color: ${Colors.primary200};
        img {
            width: 100%;
            height: 100%;
        }
    }
`;

const ProductQuickViewDetailsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    width: 88%;
    margin: 0 auto;
    ${breakpoint('desktop')`
        width: 50%;
        margin: ${ContentSpacing.space48} 0 ${ContentSpacing.space48} ${ContentSpacing.space24};
    `};
`;

const ProductSizePickerWrapper = styled(ProductItemWrapper)`
    [dir='rtl'] & {
        text-align: right;
    }
`;

const ProductRatingWrapper = styled(ProductItemWrapper)`
    margin-bottom: ${ContentSpacing.space36};
`;

const ProductNameWrapper = styled(ProductItemWrapper)`
    text-align: left;
    [dir='rtl'] & {
        text-align: right;
    }
`;

const ProductShortDescriptionWrapper = styled(ProductItemWrapper)`
    text-align: left;
    [dir='rtl'] & {
        text-align: right;
    }
`;

const ProductQuantitySelectorWrapper = styled(ProductItemWrapper)`
    width: fit-content;
    margin-bottom: ${ContentSpacing.space36};
    [dir='rtl'] & {
        text-align: right;
    }
`;

const ProductBadgeImageWrapper = styled.div`
    position: relative;
    width: 50%;
    margin: ${ContentSpacing.space48} 0 ${ContentSpacing.space48} 64px;
    [dir='rtl'] & {
        margin: ${ContentSpacing.space48} 64px ${ContentSpacing.space48} 0;
    }
`;

const QuantitySelectorAutoReplenishWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`;

const AutoReplenishWrapper = styled.div`
    width: 40%;
`;

const StyledProductBadge = styled(ProductBadge)`
    z-index: 1;
`;

const StyledPricesWrapper = styled.div`
    display: flex;
    align-items: center;
    align-self: flex-start;
`;

const FavoriteButtonWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`;

const appendDots = (dots: React.ReactChildren) => {
    return <ul className="elc-product-quick-view-dots">{dots}</ul>;
};

const ProductPricePerUnitRowWrapper = styled.div`
    margin: 0 ${ContentSpacing.space8} 0;
    ${breakpoint('desktop')`
        margin: 0;
    `};
`;

// @todo: delete these hardcoded settings once new configs make it to drupal PROD/master
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sliderSettings: { [k: string]: any } = {
    dots: true,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    responsive: [
        {
            breakpoint: Breakpoints.desktop,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1
            }
        }
    ],
    appendDots: appendDots
};

@config([
    'features',
    'shadeSwatches',
    'ratingsEnabled',
    'imageCarouselSettings',
    'isCoreSite',
    'showInventoryMessaging',
    'waitlist'
])
@translate(['freeShippingAndReturn'])
@observer
export class ProductQuickViewContent extends React.Component<IProductQuickViewContent> {
    public static displayName: string;
    @observable private carouselRef: Slider;
    @observable private thumbnailsCarouselRef: Slider;

    public setCarouselRef = (productImagesCarousel: Slider) => {
        if (!this.carouselRef) {
            this.carouselRef = productImagesCarousel;
        }

        return this.carouselRef;
    };

    public setThumbnailsCarouselRef = (thumbnailImagesCarousel: Slider) => {
        if (!this.thumbnailsCarouselRef) {
            this.thumbnailsCarouselRef = thumbnailImagesCarousel;
        }

        return this.thumbnailsCarouselRef;
    };

    private getShadeSwatches = () => {
        const { config: quickViewConfig, productShadePickerProps } = this.props;
        const { shades, onShadeSelect, productId } = productShadePickerProps;
        const shadeSwatches = quickViewConfig && quickViewConfig.shadeSwatches;
        const shadeSwatcesProductQuickshop =
            shadeSwatches && shadeSwatches.overrides && shadeSwatches.overrides.productQuickshop;
        const shadeSwatchesConfig = { ...shadeSwatches, ...shadeSwatcesProductQuickshop };
        const shadePickerType = shadeSwatchesConfig && shadeSwatchesConfig.type;
        const shadePickerProps: IShadePickerConfig = {
            shades,
            selectedShade: this.props.selectedSku.selectedShade,
            onShadeSelect,
            separator: ' ',
            productId,
            shadeSwatchesConfig
        };

        if (
            shadeSwatchesConfig &&
            shadeSwatchesConfig.enabled &&
            (shadePickerType === SHADE_PICKER_TYPE.GRID ||
                shadePickerType === SHADE_PICKER_TYPE.CAROUSEL)
        ) {
            return (
                <ProductShadePicker
                    config={shadePickerProps}
                    viewController={diContainer.get(serviceNames.productShadePickerViewController)}
                />
            );
        }
    };

    private getProductShadePickerComponent() {
        const { productShadePickerProps, isProductShaded } = this.props;

        const dropdownComponentConfig = {
            componentType: ComponentTypes.PRODUCT_QUICKSHOP,
            shadeDropdownProps: {
                ...productShadePickerProps
            },
            viewController: diContainer.get(serviceNames.productShadePickerComponentViewController)
        };

        return isProductShaded && <ProductShadePickerComponent {...dropdownComponentConfig} />;
    }

    private getProductSliderImages() {
        const { largeImages, productImageProps, product, selectedSku } = this.props;
        const imageSliderImagesWrapperClassNames = classes('image-slider-images-wrapper');
        const { config: quickViewConfig } = this.props;
        const quickViewImageCarouselSettings =
            quickViewConfig &&
            quickViewConfig.imageCarouselSettings &&
            quickViewConfig.imageCarouselSettings.productQuickshop;
        const hideImageBadge =
            quickViewImageCarouselSettings && quickViewImageCarouselSettings.hideImageBadge;

        return largeImages.map((image: IAsset, index: number) => {
            const isFirstImage = index === 0;
            const showProductBadge = !hideImageBadge && isFirstImage;

            return (
                <div key={`${image.src}-${index}`} className={imageSliderImagesWrapperClassNames}>
                    {showProductBadge && (
                        <StyledProductBadge product={product} selectedSku={selectedSku} />
                    )}
                    <ProductImages {...productImageProps} imgPath={image.src} altText={image.alt} />
                </div>
            );
        });
    }

    private getProductPriceRow() {
        const {
            formattedPrice,
            formattedOriginalPrice
        } = this.props.productPriceRowProps.selectedSku;

        return (
            <ProductPriceFormatted
                formattedPrice={formattedPrice}
                formattedOriginalPrice={formattedOriginalPrice}
            />
        );
    }

    private getProductPricePerUnit() {
        const productPricePerUnitRowWrapperClassNames = classes(
            'product-price-per-unit-row-wrapper'
        );
        const productPricePerUnitClassNames = classes('product-price-per-unit');

        const { formattedPricePerUnit } = this.props.selectedSku;

        return (
            <ProductPricePerUnitRowWrapper className={productPricePerUnitRowWrapperClassNames}>
                <PriceFormatted
                    className={productPricePerUnitClassNames}
                    formattedPrice={formattedPricePerUnit}
                />
            </ProductPricePerUnitRowWrapper>
        );
    }

    private getProductInstallmentsPrice() {
        const { selectedSku } = this.props;

        const productInstallmentPriceProps: IProductInstallmentsPrice = {
            defaultPrice: Number(selectedSku.originalPrice),
            componentType: ComponentTypes.PRODUCT_QUICKSHOP
        };

        return <ProductInstallmentsPrice {...productInstallmentPriceProps} />;
    }

    private getProductRating(ratingPropsConfig: IProductRatingConfig) {
        return <ServiceView name={PRODUCT_RATING} config={ratingPropsConfig} />;
    }

    private getPreorderMessage() {
        const {
            selectedSku: { preorderDate, isPreorderable },
            translations
        } = this.props;

        return (
            <PreorderMessage
                preorderDate={preorderDate}
                isPreorderActive={isPreorderable}
                translations={translations}
            />
        );
    }

    private getNotifyMeLink() {
        const { selectedSku, config: notifyMeConfig, translations } = this.props;
        const { enableNotifyMe } = notifyMeConfig as IProductQuickViewContentConfig;
        const { notifyWhenAvailable } = translations as ITranslationsCollection;
        const ctaType = LINK;

        const notifyMeCTAProps: IProductNotifyMeCTA = {
            selectedSku: selectedSku,
            config: {
                enableNotifyMe,
                customNotifyMeLabel: notifyWhenAvailable,
                ctaType
            }
        };

        return <NotifyMeCTA {...notifyMeCTAProps} />;
    }

    /* eslint-disable complexity */
    render() {
        const translations = this.props.translations as ITranslationsCollection;
        const { freeShippingAndReturn } = translations;

        const {
            productSizePickerProps,
            productShadePickerProps,
            productImageProps,
            shortDescriptionLocation,
            productShortDescriptionProps,
            productNameProps,
            config: quickViewConfig,
            selectedSku,
            productQuickViewImageCarouselSettings,
            isImageCarouselDisabled,
            isThumbnailsCarouselEnabled,
            largeImages,
            thumbnailsCarouselSettings,
            productAutoReplenishProps,
            product,
            ...props
        } = this.props;

        const { features, freeShippingAndReturnConfig } = this.props
            .config as IProductQuickViewContentConfig;

        const productHasSizes =
            productSizePickerProps.sizes.length > 0 &&
            productSizePickerProps.sizes.some(size => size.value !== '');

        const productHasShadesName = productShadePickerProps.shades.some(shade => shade.name);
        const productHasShades = props.isProductShaded ? productHasShadesName : false;

        const imageSliderImages = this.getProductSliderImages();

        const enableFavorites = features && features.favorites && features.favorites.enabled;
        const favoriteButtonWrapperClass = classes('favorite-button-wrapper');

        const shadeSwatches = quickViewConfig && quickViewConfig.shadeSwatches;
        const shadeSwatchesProductQuickshop =
            shadeSwatches && shadeSwatches.overrides && shadeSwatches.overrides.productQuickshop;
        const shadesConfig = { ...shadeSwatches, ...shadeSwatchesProductQuickshop };
        const shadeSwatchesEnabled = shadesConfig && shadesConfig.enabled;

        const freeShippingAndReturnEnabled =
            freeShippingAndReturnConfig && freeShippingAndReturnConfig.enabled;

        const displayShadePicker =
            shadeSwatchesEnabled && productHasShades
                ? shadeSwatchesEnabled && productHasShades
                : productHasShades;

        const ratingsEnabled = quickViewConfig && quickViewConfig.ratingsEnabled;

        const imageCarouselSettings = {
            ...DEFAULT_IMAGE_CAROUSEL_SETTINGS,
            ...sliderSettings, // will be removed once new config reaches PROD
            ...productQuickViewImageCarouselSettings
        };
        const isCoreSite = quickViewConfig && quickViewConfig.isCoreSite;

        const ctaConfig: IProductCTAConfig = {
            ...props.productAddToBagProps.config,
            closeModalAction: props.onCloseButtonClick,
            skuId: selectedSku.skuId,
            autoReplenishOptions: props.autoReplenishOptions,
            isCoreSite
        };

        const displayInvMessage =
            !isCoreSite && (!ctaConfig.ctaType || ctaConfig.ctaType === ADD_TO_BAG);

        const isThumbnailsSliderVertical = thumbnailsCarouselSettings
            ? thumbnailsCarouselSettings.vertical
            : DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS.vertical;

        const thumbnailsCarouselConfig = {
            imageCarouselSettings
        };

        const productQuickViewContentClassNames = classes('product-quick-view-content');
        const quickViewContentPricesWrapperClassNames = classes('quickview-content-prices-wrapper');
        const carouselWrapperClassNames = classes('carousel-section-wrapper');
        const showCarousel = !isThumbnailsCarouselEnabled && !isImageCarouselDisabled;
        const { taxIncluded = '' } = translations as ITranslationsCollection;
        const taxIncludedLabelWrapper = classes('product-tax-included-label-wrapper');

        const displayNotifyMeLink = selectedSku.isPreorderEnabled && !selectedSku.isShoppable;

        return (
            <ProductQuickViewContentWrapper className={productQuickViewContentClassNames}>
                <ProductBadgeImageWrapper className="elc-product-badge-image-wrapper">
                    <StyledProductBadge
                        className="elc-product-badge-above-slider"
                        product={product}
                        selectedSku={selectedSku}
                    />
                    {showCarousel && (
                        <SliderView
                            sliderSettings={imageCarouselSettings}
                            items={imageSliderImages}
                        />
                    )}
                    {isThumbnailsCarouselEnabled && (
                        <StyledSliderSection
                            isThumbnailsSliderVertical={isThumbnailsSliderVertical}
                            showThumbnails={isThumbnailsCarouselEnabled}
                            className={carouselWrapperClassNames}
                        >
                            <ProductThumbnailsCarousel
                                config={thumbnailsCarouselConfig}
                                sliderImages={largeImages}
                                desktopCarouselSettings={imageCarouselSettings}
                                isThumbnailsSliderVertical={isThumbnailsSliderVertical}
                                sliderItems={imageSliderImages}
                                carouselRef={this.carouselRef}
                                thumbnailsCarouselRef={this.thumbnailsCarouselRef}
                                setCarouselRef={this.setCarouselRef}
                                setThumbnailsCarouselRef={this.setThumbnailsCarouselRef}
                            />
                        </StyledSliderSection>
                    )}
                </ProductBadgeImageWrapper>

                <ProductQuickViewDetailsWrapper className="elc-product-quick-view-details-wrapper">
                    <ProductNameWrapper component="info-name">
                        <ProductName {...productNameProps} />
                    </ProductNameWrapper>
                    {shortDescriptionLocation &&
                        shortDescriptionLocation.quickshop &&
                        productShortDescriptionProps &&
                        productShortDescriptionProps.description && (
                            <ProductShortDescriptionWrapper component="info-short-description">
                                <ProductDescription {...productShortDescriptionProps} />
                            </ProductShortDescriptionWrapper>
                        )}
                    {!props.hideProductRating && (
                        // @todo refactor the naming of the Power Reviews component: https://jira.esteeonline.com/browse/SPPSELTO-2930
                        <ProductRatingWrapper component="rating">
                            {ratingsEnabled ? (
                                this.getProductRating(props.productRatingProps)
                            ) : (
                                <ServiceView
                                    name={PRODUCT_RATING_WRAPPER}
                                    config={props.productRatingProps}
                                />
                            )}
                        </ProductRatingWrapper>
                    )}

                    {productHasSizes && (
                        <ProductSizePickerWrapper component="size-picker">
                            <>
                                {taxIncluded && (
                                    <div className={taxIncludedLabelWrapper}>{taxIncluded}</div>
                                )}
                                <ProductSizePicker {...productSizePickerProps} />
                            </>
                        </ProductSizePickerWrapper>
                    )}

                    {displayShadePicker && this.getShadeSwatches()}
                    {this.getProductShadePickerComponent()}

                    <QuantitySelectorAutoReplenishWrapper>
                        {props.showProductQuantitySelector && (
                            <Desktop minWidth={Breakpoints.desktop}>
                                {() => (
                                    <>
                                        <ProductQuantitySelectorWrapper component="quantity">
                                            <ProductQuantitySelector
                                                {...props.productQuantitySelectorProps}
                                            />
                                        </ProductQuantitySelectorWrapper>
                                    </>
                                )}
                            </Desktop>
                        )}
                        {props.isProductReplenishable && (
                            <AutoReplenishWrapper className="elc-quick-view-auto-replenish-wrapper">
                                <ProductAutoReplenish {...productAutoReplenishProps} />
                            </AutoReplenishWrapper>
                        )}
                    </QuantitySelectorAutoReplenishWrapper>

                    <StyledPricesWrapper className={quickViewContentPricesWrapperClassNames}>
                        <ProductItemWrapper component="price">
                            {this.getProductPriceRow()}
                        </ProductItemWrapper>
                        {!props.hideProductPricePerUnit && (
                            <ProductItemWrapper component="price-per-unit">
                                {this.getProductPricePerUnit()}
                            </ProductItemWrapper>
                        )}
                    </StyledPricesWrapper>

                    {enableFavorites && (
                        <FavoriteButtonWrapper className={favoriteButtonWrapperClass}>
                            <FavoriteButton
                                isFavorite={selectedSku.isFavorite}
                                handleFavoriteClick={props.handleFavoriteClick}
                            />
                        </FavoriteButtonWrapper>
                    )}
                    <ProductItemWrapper component="cta">
                        <ProductCTA
                            config={ctaConfig}
                            viewController={diContainer.get(serviceNames.productCTAViewController)}
                        />
                    </ProductItemWrapper>
                    {selectedSku.isPreorderEnabled && this.getPreorderMessage()}
                    {displayNotifyMeLink && this.getNotifyMeLink()}
                    {this.getProductInstallmentsPrice()}
                    {displayInvMessage && (
                        <InventoryStatusMessage
                            config={this.props.config}
                            inventoryStatus={selectedSku.inventoryStatus}
                        />
                    )}
                    {freeShippingAndReturnEnabled && (
                        <ProductItemWrapper component="free-shipping-and-return">
                            {freeShippingAndReturn}
                        </ProductItemWrapper>
                    )}
                </ProductQuickViewDetailsWrapper>
            </ProductQuickViewContentWrapper>
        );
    }
}

ProductQuickViewContent.displayName = 'ProductQuickViewContent';
